import {
  createClient,
  FunctionsHttpError,
  type Session,
  type SupabaseClient,
} from '@supabase/supabase-js';

export const packageId = '@ai-factory/auth-sdk' as const;
export const PASSWORD_MIN_LENGTH = 8;
// GoTrue hashes passwords with bcrypt, whose input is limited to 72 UTF-8
// bytes. Validate at the shared client boundary so an oversized password never
// reaches Auth as an opaque server failure.
export const PASSWORD_MAX_UTF8_BYTES = 72;

export type ApplicationCode =
  | 'platform_console'
  | 'home_visual_consumer'
  | 'product_visual_business'
  | 'sales_agent_business'
  | 'tarot_consumer';

export type AuthAudience = 'consumer' | 'business' | 'internal';

export interface AuthRuntimeConfig {
  applicationCode: ApplicationCode;
  audience: AuthAudience;
  supabaseUrl: string;
  supabasePublishableKey: string;
  siteUrl: string;
}

export interface ProvisionInput {
  accountName: string;
  organizationName?: string;
  locale?: string;
  timezone?: string;
  countryCode?: string;
}

export interface ActivationResult {
  allowed: boolean;
  reason: string;
  application_code: ApplicationCode;
  app_account_id: string | null;
  account_status: string | null;
  member_status: string | null;
  onboarding_required: boolean;
  refresh_session: boolean;
}

export interface InvitationDetails {
  invitation_id: string;
  application_code: ApplicationCode;
  app_account_id: string;
  account_name: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expires_at: string;
}

interface FunctionPayload<T> {
  data?: T;
  error?: string;
}

const browserClients = new Map<string, SupabaseClient>();
type AuthFunctionEndpoint =
  | 'auth-activate-application'
  | 'auth-provision-account'
  | 'auth-create-invitation'
  | 'auth-get-invitation'
  | 'auth-accept-invitation'
  | 'auth-resend-invitation'
  | 'auth-revoke-invitation';

function authDispatcherSlug(endpoint: AuthFunctionEndpoint): string {
  return endpoint === 'auth-get-invitation'
    ? 'auth-get-invitation'
    : 'core-create-organization';
}

async function functionErrorCode(error: unknown): Promise<string | null> {
  if (!(error instanceof FunctionsHttpError) || !(error.context instanceof Response)) return null;
  const payload = await error.context.clone().json().catch(() => null) as FunctionPayload<unknown> | null;
  return typeof payload?.error === 'string' ? payload.error : null;
}

/**
 * The application code is the stable source of truth for the sign-in
 * experience. Shared UI packages use this instead of creating a second
 * anonymous Supabase browser client beside the Auth page.
 */
export function authAudienceForApplication(applicationCode: ApplicationCode): AuthAudience {
  switch (applicationCode) {
    case 'platform_console':
      return 'internal';
    case 'product_visual_business':
    case 'sales_agent_business':
      return 'business';
    case 'home_visual_consumer':
    case 'tarot_consumer':
      return 'consumer';
  }
}

export function passwordValidationError(password: string, requireMinimumLength = false): string | null {
  if (new TextEncoder().encode(password).byteLength > PASSWORD_MAX_UTF8_BYTES) {
    return 'password_too_long';
  }
  if (requireMinimumLength && password.length < PASSWORD_MIN_LENGTH) {
    return 'password_too_short';
  }
  return null;
}

function assertValidPassword(password: string, requireMinimumLength = false): void {
  const error = passwordValidationError(password, requireMinimumLength);
  if (error) throw new Error(error);
}

export function createAuthClient(config: AuthRuntimeConfig): SupabaseClient {
  const key = `${config.supabaseUrl}:${config.supabasePublishableKey}`;
  const existing = browserClients.get(key);
  if (existing) return existing;
  const client = createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  });
  browserClients.set(key, client);
  return client;
}

/**
 * Builds the application-local callback used in Supabase emails. Keeping an
 * explicitly selected locale on the callback prevents a visitor from
 * returning to a different-language verification or recovery screen.
 */
export function authCallbackUrl(
  config: AuthRuntimeConfig,
  options: { locale?: string; mode?: 'recovery' } = {},
): string {
  const callback = new URL(`${config.siteUrl.replace(/\/$/, '')}/auth/callback`);
  if (options.locale) callback.searchParams.set('locale', options.locale);
  if (options.mode) callback.searchParams.set('mode', options.mode);
  return callback.toString();
}

async function invoke<T>(
  client: SupabaseClient,
  name: AuthFunctionEndpoint,
  body: Record<string, unknown>,
): Promise<T> {
  const physicalEndpoint = authDispatcherSlug(name);
  const { data, error } = await client.functions.invoke<FunctionPayload<T>>(physicalEndpoint, {
    body,
    headers: physicalEndpoint === name ? {} : { 'x-ai-factory-endpoint': name },
  });
  if (error) throw new Error((await functionErrorCode(error)) ?? error.message);
  if (!data?.data) throw new Error(data?.error ?? `${name}_failed`);
  return data.data;
}

export async function activateApplication(
  client: SupabaseClient,
  config: AuthRuntimeConfig,
): Promise<ActivationResult> {
  let result: ActivationResult;
  try {
    result = await invoke<ActivationResult>(client, 'auth-activate-application', {
      application_code: config.applicationCode,
    });
  } catch (error) {
    const reason = error instanceof Error ? error.message : 'access_denied';
    if (config.audience === 'internal' && reason === 'platform_console_invitation_required') {
      return {
        allowed: false,
        reason,
        application_code: config.applicationCode,
        app_account_id: null,
        account_status: null,
        member_status: null,
        onboarding_required: false,
        refresh_session: false,
      };
    }
    throw error;
  }
  if (result.refresh_session) {
    const refreshed = await client.auth.refreshSession();
    if (refreshed.error) throw refreshed.error;
  }
  return result;
}

export async function provisionAccount(
  client: SupabaseClient,
  config: AuthRuntimeConfig,
  input: ProvisionInput,
): Promise<Record<string, unknown>> {
  return invoke(client, 'auth-provision-account', {
    application_code: config.applicationCode,
    account_name: input.accountName,
    organization_name: input.organizationName,
    locale: input.locale ?? navigator.language ?? 'en-US',
    timezone: input.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
    country_code: input.countryCode,
  });
}

export async function signInWithPassword(
  client: SupabaseClient,
  config: AuthRuntimeConfig,
  email: string,
  password: string,
  invitationToken?: string | null,
): Promise<ActivationResult> {
  assertValidPassword(password);
  const signedIn = await client.auth.signInWithPassword({ email, password });
  if (signedIn.error) throw signedIn.error;
  if (invitationToken) await acceptInvitation(client, config, invitationToken);
  // Platform Console accounts have no application account to provision. When
  // the signed-in JWT is already scoped to this internal application, avoid a
  // redundant activation round trip; the console API still independently
  // verifies the active platform-admin profile before serving any data.
  if (
    config.audience === 'internal' &&
    signedIn.data.session?.user.app_metadata.application_code === config.applicationCode
  ) {
    return {
      allowed: true,
      reason: 'already_active',
      application_code: config.applicationCode,
      app_account_id: null,
      account_status: null,
      member_status: null,
      onboarding_required: false,
      refresh_session: false,
    };
  }
  return activateApplication(client, config);
}

export async function sendMagicLink(
  client: SupabaseClient,
  config: AuthRuntimeConfig,
  email: string,
  locale?: string,
): Promise<void> {
  const result = await client.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: false,
      emailRedirectTo: authCallbackUrl(config, locale ? { locale } : {}),
      data: { application_code: config.applicationCode },
    },
  });
  if (result.error) throw result.error;
}

export async function signUp(
  client: SupabaseClient,
  config: AuthRuntimeConfig,
  email: string,
  password: string,
  input: ProvisionInput,
  invitationToken?: string | null,
): Promise<{
  session: Session | null;
  requiresEmailVerification: boolean;
  activation: ActivationResult | null;
}> {
  assertValidPassword(password, true);
  const result = await client.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: authCallbackUrl(config, input.locale ? { locale: input.locale } : {}),
      data: {
        application_code: config.applicationCode,
        account_name: input.accountName,
        organization_name: input.organizationName,
        invitation_token: invitationToken,
        ...(input.locale ? { locale: input.locale } : {}),
        ...(input.timezone ? { timezone: input.timezone } : {}),
      },
    },
  });
  if (result.error) throw result.error;
  let activation: ActivationResult | null = null;
  if (result.data.session) {
    if (invitationToken) await acceptInvitation(client, config, invitationToken);
    else await provisionAccount(client, config, input);
    activation = await activateApplication(client, config);
  }
  return {
    session: result.data.session,
    requiresEmailVerification: !result.data.session,
    activation,
  };
}

export async function completeAuthCallback(
  client: SupabaseClient,
  config: AuthRuntimeConfig,
  code: string,
): Promise<ActivationResult> {
  const exchanged = await client.auth.exchangeCodeForSession(code);
  if (exchanged.error) throw exchanged.error;
  const user = exchanged.data.user;
  const metadata = user.user_metadata as Record<string, unknown>;
  const invitationToken =
    typeof metadata.invitation_token === 'string' ? metadata.invitation_token : null;
  if (invitationToken) await acceptInvitation(client, config, invitationToken);
  let activation = await activateApplication(client, config);
  if (activation.onboarding_required && !invitationToken) {
    await provisionAccount(client, config, {
      accountName:
        typeof metadata.account_name === 'string'
          ? metadata.account_name
          : (user.email?.split('@')[0] ?? 'My account'),
      ...(typeof metadata.organization_name === 'string'
        ? { organizationName: metadata.organization_name }
        : {}),
      ...(typeof metadata.locale === 'string' ? { locale: metadata.locale } : {}),
      ...(typeof metadata.timezone === 'string' ? { timezone: metadata.timezone } : {}),
    });
    activation = await activateApplication(client, config);
  }
  return activation;
}

export async function completePasswordRecovery(
  client: SupabaseClient,
  code: string | null,
  implicitAccessToken: string | null,
): Promise<void> {
  if (code) {
    const exchanged = await client.auth.exchangeCodeForSession(code);
    if (exchanged.error) throw exchanged.error;
    return;
  }

  if (!implicitAccessToken) throw new Error('RECOVERY_LINK_INVALID');
  // Supabase's default implicit flow consumes the URL fragment when the client
  // initializes. Confirm that it established the session from this link, not
  // from a previously signed-in account in browser storage.
  const { data, error } = await client.auth.getSession();
  if (error) throw error;
  if (!data.session || data.session.access_token !== implicitAccessToken) {
    throw new Error('RECOVERY_LINK_INVALID');
  }
}

export async function updatePassword(client: SupabaseClient, password: string): Promise<void> {
  assertValidPassword(password, true);
  const result = await client.auth.updateUser({ password });
  if (result.error) throw result.error;
}

export async function requestPasswordReset(
  client: SupabaseClient,
  config: AuthRuntimeConfig,
  email: string,
  locale?: string,
): Promise<void> {
  const result = await client.auth.resetPasswordForEmail(email, {
    redirectTo: authCallbackUrl(config, {
      ...(locale ? { locale } : {}),
      mode: 'recovery',
    }),
  });
  if (result.error) throw result.error;
}

export async function signOut(client: SupabaseClient): Promise<void> {
  const result = await client.auth.signOut();
  if (result.error) throw result.error;
}

export async function getInvitation(
  config: AuthRuntimeConfig,
  token: string,
): Promise<InvitationDetails> {
  const url = new URL(`${config.supabaseUrl.replace(/\/$/, '')}/functions/v1/auth-get-invitation`);
  url.searchParams.set('token', token);
  const response = await fetch(url, { headers: { apikey: config.supabasePublishableKey } });
  const payload = (await response.json()) as FunctionPayload<InvitationDetails>;
  if (!response.ok || !payload.data) throw new Error(payload.error ?? 'invitation_not_found');
  return payload.data;
}

export async function acceptInvitation(
  client: SupabaseClient,
  config: AuthRuntimeConfig,
  token: string,
): Promise<Record<string, unknown>> {
  return invoke(client, 'auth-accept-invitation', {
    token,
    application_code: config.applicationCode,
  });
}

export { AuthPage, type AuthBrand, type AuthScreen } from './ui';
